import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Layers, Plus, Trash2, Zap } from 'lucide-react';
import { adminSession, adminTradingGroups } from '@/api/admin';
import type {
  CreateTradingGroupRequest,
  SessionWindowIssue,
  TradingGroup,
  TradingGroupPendingConfig,
  UpdateTradingGroupRequest,
  WeekDay,
} from '@/api/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Pill } from '@/components/widgets/Pill';
import { toast } from '@/components/ui/sonner';
import { ApiError } from '@/lib/http';
import { fmtDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import { TimeField, ValidationBanner, fmtHM } from './SessionConfig';

const ALL_DAYS: WeekDay[] = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DAY_ORDER: Record<WeekDay, number> = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };
const orderDays = (a: WeekDay, b: WeekDay) => DAY_ORDER[a] - DAY_ORDER[b];

// ─── 校验内核(纯函数,便于单测)────────────────────────────────────

/** 组窗口的四个边界(时:分)。 */
export interface GroupWindow {
  openHour: number;
  openMinute: number;
  windDownHour: number;
  windDownMinute: number;
  liquidationHour: number;
  liquidationMinute: number;
  forceCloseHour: number;
  forceCloseMinute: number;
}

/** 时:分 → 当日分钟数。 */
export function toMinutes(hour: number, minute?: number): number {
  return hour * 60 + (minute ?? 0);
}

/**
 * 相对开仓的**前向偏移**:off(x) = (x − open) mod 1440,结果 0 记作 1440(整整一圈)。
 * 全局时段(19:00 开仓、次日 03:00 兜底)与不跨零点的组窗口(如 14:00–17:00)统一用这一把尺子。
 */
function forwardOffset(target: number, open: number): number {
  const raw = ((target - open) % 1440 + 1440) % 1440;
  return raw === 0 ? 1440 : raw;
}

/**
 * 时刻顺序校验。注意用的是**环序**不是「分钟序」:
 * 组窗口可以不跨零点(A 组 14–17、B 组 17–20、默认组 21–24),此时开仓的分钟数比收尾/结算/兜底都小,
 * 直接按分钟大小比会把这三组全判成非法。判据统一为「相对开仓的前向偏移依次不减」:
 * 0 < off(收尾) ≤ off(结算) ≤ off(兜底) ≤ 1440。跨零点的旧全局口径(19:00→次日 03:00)是它的真子集。
 *
 * @returns null = 合法;否则返回中文原因。
 */
export function checkGroupOrder(w: GroupWindow): string | null {
  const open = toMinutes(w.openHour, w.openMinute);
  const wd = forwardOffset(toMinutes(w.windDownHour, w.windDownMinute), open);
  const liq = forwardOffset(toMinutes(w.liquidationHour, w.liquidationMinute), open);
  const fc = forwardOffset(toMinutes(w.forceCloseHour, w.forceCloseMinute), open);
  if (wd > liq) return '收尾开始必须早于结算时刻(从开仓时刻起向后推算)';
  if (liq > fc) return '结算时刻必须早于兜底时刻(从开仓时刻起向后推算)';
  return null;
}

/**
 * 组窗口必须**完整落在**全局交易日 `[全局兜底, 次日全局兜底)` 内。
 * 交易日界不分组:daily_pnl / 考核 / 胜率 / 单日亏损急停全部锚在全局兜底上,组只决定「什么时候能交易」。
 * 判据:把全局兜底当成 0 点,组的开仓必须排在组的兜底之前(组兜底恰等于全局兜底时视为交易日末尾)。
 */
export function windowWithinGlobalTradeDay(w: GroupWindow, globalForceCloseMinutes: number): boolean {
  const rel = (m: number) => ((m - globalForceCloseMinutes) % 1440 + 1440) % 1440;
  const openRel = rel(toMinutes(w.openHour, w.openMinute));
  const fcRaw = rel(toMinutes(w.forceCloseHour, w.forceCloseMinute));
  // 组兜底 == 全局兜底 → 落在交易日的最末端(1440),不是「第 0 分钟」。
  const fcRel = fcRaw === 0 ? 1440 : fcRaw;
  return openRel < fcRel;
}

/** 列表里的窗口标签:24 小时组特判,其余显示「开仓-兜底」。 */
export function windowLabel(g: Pick<TradingGroup, 'alwaysOn' | 'openHour' | 'openMinute' | 'forceCloseHour' | 'forceCloseMinute'>): string {
  if (g.alwaysOn) return '24 小时(持续开放)';
  return `${fmtHM(g.openHour, g.openMinute)}-${fmtHM(g.forceCloseHour, g.forceCloseMinute)}`;
}

// ─── 页面 ─────────────────────────────────────────────────────────

export function TradingGroups() {
  const qc = useQueryClient();
  const listQ = useQuery({ queryKey: ['admin', 'trading-groups'], queryFn: adminTradingGroups.list });
  // 全局时段:既用来渲染「默认(全局)」那一行,也提供「窗口须落在全局交易日内」的基准(全局兜底)。
  const sessQ = useQuery({ queryKey: ['admin', 'trading-session'], queryFn: adminSession.get });
  const globalFcMinutes = sessQ.data
    ? toMinutes(sessQ.data.forceCloseHour, sessQ.data.forceCloseMinute)
    : null;

  const [formTarget, setFormTarget] = useState<{ mode: 'create' } | { mode: 'edit'; group: TradingGroup } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TradingGroup | null>(null);
  const [applyTarget, setApplyTarget] = useState<TradingGroup | null>(null);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['admin', 'trading-groups'] });
    // 绑定关系 / 组名变化会改变邀请码页与用户列表的「时段组」列。
    void qc.invalidateQueries({ queryKey: ['admin', 'invite-codes'] });
    void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
  };

  // 启停:管理属性,恒立即生效,不进待生效。
  const toggle = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      adminTradingGroups.update(id, { enabled }),
    onSuccess: (_, vars) => {
      toast.success(vars.enabled ? '时段组已启用' : '时段组已停用', {
        description: vars.enabled
          ? undefined
          : '不再为该组调度收盘 / 倒计时;组内用户的时段解析回退到全局配置,绑定关系保留',
      });
      invalidate();
    },
    onError: (err) => toast.error('操作失败', { description: apiMessage(err) }),
  });

  const items = listQ.data?.items ?? [];

  return (
    <div className="p-6 space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-ink-0 flex items-center gap-2">
            <Layers className="h-5 w-5 text-brand" />
            交易时段组(账户分层)
          </h1>
          <p className="text-sm text-ink-2 mt-1">
            把可交易窗口按渠道(邀请码)/ 单用户拆成多组。组<strong>只决定「什么时候能交易」</strong>,
            不决定「算哪一天」—— 交易日界仍是全局兜底翻篇,分组盈亏只是按组聚合同一交易日的数据。
          </p>
        </div>
        <Button onClick={() => setFormTarget({ mode: 'create' })}>
          <Plus className="h-4 w-4" />
          新建时段组
        </Button>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>时段组列表 · {items.length} 组</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {listQ.isError && (
            <div className="px-4 py-3 text-sm text-down-dim">
              无法加载时段组:{apiMessage(listQ.error)}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-bg-3 text-ink-2 text-xs">
                <tr>
                  <th className="px-3 py-2 text-left font-normal">组名</th>
                  <th className="px-3 py-2 text-left font-normal">窗口(开仓-兜底)</th>
                  <th className="px-3 py-2 text-left font-normal">交易日</th>
                  <th className="px-3 py-2 text-right font-normal" title="按解析优先级最终落到本组的用户数(单用户覆盖 + 该组名下渠道带来的用户)">
                    用户数
                  </th>
                  <th className="px-3 py-2 text-left font-normal">绑定渠道</th>
                  <th className="px-3 py-2 text-left font-normal">待生效</th>
                  <th className="px-3 py-2 text-left font-normal">启用</th>
                  <th className="px-3 py-2 text-right font-normal">操作</th>
                </tr>
              </thead>
              <tbody>
                {/* 「未分组」不是一条记录(没有 id),但必须让运营看得见:它的窗口就是全局时段配置。 */}
                <tr className="border-t border-line bg-bg-2/40">
                  <td className="px-3 py-2 text-ink-1">
                    默认(全局)
                    <span className="ml-1.5 text-xs text-ink-3">未绑定任何组的用户</span>
                  </td>
                  <td className="px-3 py-2 num text-ink-2">
                    {sessQ.data
                      ? `${fmtHM(sessQ.data.openHour, sessQ.data.openMinute)}-${fmtHM(sessQ.data.forceCloseHour, sessQ.data.forceCloseMinute)}`
                      : '—'}
                  </td>
                  <td className="px-3 py-2 text-xs text-ink-2">
                    {sessQ.data?.tradingDays.join(' ') ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-right num text-ink-1">
                    {listQ.data?.ungroupedUserCount ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-ink-3">—</td>
                  <td className="px-3 py-2">
                    {sessQ.data?.pendingChange ? <Pill tone="warn">有待生效</Pill> : <span className="text-ink-3">—</span>}
                  </td>
                  <td className="px-3 py-2 text-ink-3 text-xs">恒启用</td>
                  <td className="px-3 py-2 text-right">
                    <Link to="/session" className="text-xs text-brand hover:underline">
                      去「交易时段配置」
                    </Link>
                  </td>
                </tr>

                {items.map((g) => (
                  <tr key={g.id} className="border-t border-line row-hover">
                    <td className="px-3 py-2 text-ink-1">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="font-medium">{g.name}</span>
                        {g.isDefault && (
                          <span title="兜底组:只作管理端预选默认项,不参与解析优先级(未绑定任何组的用户仍走全局)">
                            <Pill tone="brand">兜底组</Pill>
                          </span>
                        )}
                        {g.alwaysOn && (
                          <span title="24 小时组:恒可交易、不调度收盘 / 倒计时、额度重置整组排除">
                            <Pill tone="warn">持续开放</Pill>
                          </span>
                        )}
                      </div>
                    </td>
                    <td
                      className="px-3 py-2 num text-ink-1"
                      title={
                        g.alwaysOn
                          ? '持续开放组不参与日终结算'
                          : `开仓 ${fmtHM(g.openHour, g.openMinute)} → 收尾 ${fmtHM(g.windDownHour, g.windDownMinute)} → 结算 ${fmtHM(g.liquidationHour, g.liquidationMinute)} → 兜底 ${fmtHM(g.forceCloseHour, g.forceCloseMinute)}`
                      }
                    >
                      {windowLabel(g)}
                    </td>
                    <td className="px-3 py-2 text-xs text-ink-2">
                      {g.alwaysOn ? <span className="text-ink-3">不适用</span> : g.tradingDays.join(' ')}
                    </td>
                    <td className="px-3 py-2 text-right num text-ink-1">{g.userCount}</td>
                    <td className="px-3 py-2 text-xs">
                      {g.boundInviteCodes.length === 0 ? (
                        <span className="text-ink-3">—</span>
                      ) : (
                        <div className="flex flex-wrap items-center gap-1">
                          {g.boundInviteCodes.map((c) => (
                            <span
                              key={c.id}
                              className={cn(
                                'rounded border border-line bg-bg-3 px-1.5 py-0.5 font-mono tracking-wider',
                                c.enabled ? 'text-ink-1' : 'text-ink-3 line-through',
                              )}
                              title={`${c.alias ?? '未设别名'}${c.enabled ? '' : ' · 该渠道已停用'}`}
                            >
                              {c.code}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {g.pendingConfig ? (
                        <div className="space-y-0.5">
                          <Pill tone="warn">
                            有待生效
                            {g.pendingEffectiveAt ? ` · ${fmtDateTime(g.pendingEffectiveAt)}` : ''}
                          </Pill>
                          <PendingGroupDiff group={g} />
                        </div>
                      ) : (
                        <span className="text-ink-3">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <Switch
                        checked={g.enabled}
                        disabled={toggle.isPending}
                        onCheckedChange={(v) => toggle.mutate({ id: g.id, enabled: v })}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1.5">
                        {g.pendingConfig && (
                          <Button variant="warn" size="sm" onClick={() => setApplyTarget(g)}>
                            <Zap className="h-3.5 w-3.5" />
                            立即生效
                          </Button>
                        )}
                        <Button variant="outline" size="sm" onClick={() => setFormTarget({ mode: 'edit', group: g })}>
                          编辑
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-down"
                          onClick={() => setDeleteTarget(g)}
                          title="删除前须先解绑全部渠道与用户"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          删除
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}

                {items.length === 0 && !listQ.isLoading && (
                  <tr>
                    <td colSpan={8} className="px-3 py-8 text-center text-ink-3">
                      还没有时段组,全部用户走上面的「默认(全局)」窗口。点右上角新建第一个分层组。
                    </td>
                  </tr>
                )}
                {listQ.isLoading && (
                  <tr>
                    <td colSpan={8} className="px-3 py-8 text-center text-ink-3">
                      加载中...
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="py-4 text-xs text-ink-3 leading-relaxed space-y-1">
          <div>
            · <strong className="text-ink-2">窗口约束</strong>:每个组的「开仓 → 兜底」必须完整落在全局交易日
            {globalFcMinutes != null && sessQ.data
              ? `(${fmtHM(sessQ.data.forceCloseHour, sessQ.data.forceCloseMinute)} 起算的 24 小时)`
              : ''}
            内,否则保存会被拒(INVALID_GROUP_WINDOW)。
          </div>
          <div>
            · <strong className="text-ink-2">组收盘 ≠ 全局强平</strong>:组到点只做本组用户的虚拟腿日终结算,
            真实层敞口交由 30s 净额对账收敛;不置 emergency_stop、不影响其他组。
          </div>
          <div>
            · <strong className="text-ink-2">容量与平台级风控仍是全平台共享</strong>:下午组开仓会挤占晚上组的容量额度;
            平台级急停触发时所有组(含持续开放组)一并平仓。
          </div>
          <div>
            · <strong className="text-ink-2">改绑不回溯</strong>:虚拟腿的组归属在开仓那一刻写死,改渠道 / 改个人覆盖后历史分组日报不漂移。
          </div>
        </CardContent>
      </Card>

      <GroupFormDialog
        target={formTarget}
        globalForceCloseMinutes={globalFcMinutes}
        globalForceCloseLabel={
          sessQ.data ? fmtHM(sessQ.data.forceCloseHour, sessQ.data.forceCloseMinute) : null
        }
        onClose={() => setFormTarget(null)}
        onDone={invalidate}
      />

      <DeleteGroupDialog target={deleteTarget} onClose={() => setDeleteTarget(null)} onDone={invalidate} />

      <ApplyGroupPendingDialog target={applyTarget} onClose={() => setApplyTarget(null)} onDone={invalidate} />
    </div>
  );
}

// ─── 待生效 diff ──────────────────────────────────────────────────

const PENDING_TIME_ROWS: Array<[string, keyof TradingGroupPendingConfig, keyof TradingGroupPendingConfig]> = [
  ['开仓', 'openHour', 'openMinute'],
  ['收尾', 'windDownHour', 'windDownMinute'],
  ['结算', 'liquidationHour', 'liquidationMinute'],
  ['兜底', 'forceCloseHour', 'forceCloseMinute'],
];

/** 待生效 diff(只列真正变化的字段;后端只回填改过的键)。 */
function PendingGroupDiff({ group }: { group: TradingGroup }) {
  const p = group.pendingConfig;
  if (!p) return null;
  const rows: string[] = [];
  for (const [label, hKey, mKey] of PENDING_TIME_ROWS) {
    const nextH = p[hKey] as number | undefined;
    const nextM = p[mKey] as number | undefined;
    if (nextH == null && nextM == null) continue;
    const curH = group[hKey as 'openHour'];
    const curM = group[mKey as 'openMinute'];
    const effH = nextH ?? curH;
    const effM = nextM ?? curM;
    if (effH === curH && effM === curM) continue;
    rows.push(`${label} ${fmtHM(curH, curM)}→${fmtHM(effH, effM)}`);
  }
  if (p.alwaysOn != null && p.alwaysOn !== group.alwaysOn) {
    rows.push(`持续开放 ${group.alwaysOn ? '开' : '关'}→${p.alwaysOn ? '开' : '关'}`);
  }
  if (p.tradingDays && p.tradingDays.join(' ') !== group.tradingDays.join(' ')) {
    rows.push(`交易日 ${group.tradingDays.join('')}→${p.tradingDays.join('')}`);
  }
  if (rows.length === 0) return null;
  return <div className="text-[11px] num text-warn-dim">{rows.join(' · ')}</div>;
}

// ─── 新建 / 编辑表单 ───────────────────────────────────────────────

export interface GroupDraft extends GroupWindow {
  name: string;
  enabled: boolean;
  isDefault: boolean;
  alwaysOn: boolean;
  tradingDays: WeekDay[];
}

/** 新建时的默认草稿(首批投产的「默认组 21-24」形状)。 */
function emptyDraft(): GroupDraft {
  return {
    name: '',
    enabled: true,
    isDefault: false,
    alwaysOn: false,
    openHour: 21,
    openMinute: 0,
    windDownHour: 23,
    windDownMinute: 0,
    liquidationHour: 23,
    liquidationMinute: 30,
    forceCloseHour: 23,
    forceCloseMinute: 50,
    tradingDays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
  };
}

function draftFromGroup(g: TradingGroup): GroupDraft {
  return {
    name: g.name,
    enabled: g.enabled,
    isDefault: g.isDefault,
    alwaysOn: g.alwaysOn,
    openHour: g.openHour,
    openMinute: g.openMinute ?? 0,
    windDownHour: g.windDownHour,
    windDownMinute: g.windDownMinute ?? 0,
    liquidationHour: g.liquidationHour,
    liquidationMinute: g.liquidationMinute ?? 0,
    forceCloseHour: g.forceCloseHour,
    forceCloseMinute: g.forceCloseMinute ?? 0,
    tradingDays: [...g.tradingDays],
  };
}

/** 窗口类字段(默认落待生效)。name / enabled / isDefault 是管理属性,恒立即生效。 */
const WINDOW_KEYS = [
  'alwaysOn',
  'openHour',
  'openMinute',
  'windDownHour',
  'windDownMinute',
  'liquidationHour',
  'liquidationMinute',
  'forceCloseHour',
  'forceCloseMinute',
] as const;

/** 只把真正改动的字段发出去 —— 否则未改的窗口字段也会被写成待生效,凭空造出一条 pending。 */
export function diffGroupDraft(
  draft: GroupDraft,
  g: TradingGroup,
): { body: UpdateTradingGroupRequest; windowChanged: boolean; changed: boolean } {
  const body: UpdateTradingGroupRequest = {};
  if (draft.name.trim() !== g.name) body.name = draft.name.trim();
  if (draft.enabled !== g.enabled) body.enabled = draft.enabled;
  if (draft.isDefault !== g.isDefault) body.isDefault = draft.isDefault;

  let windowChanged = false;
  for (const k of WINDOW_KEYS) {
    const next = draft[k];
    const cur = g[k];
    if (next !== cur) {
      // 各键类型一一对应(bool ↔ bool / number ↔ number),此处窄化交给 UpdateTradingGroupRequest。
      (body as Record<string, unknown>)[k] = next;
      windowChanged = true;
    }
  }
  const daysChanged = draft.tradingDays.join(' ') !== g.tradingDays.join(' ');
  if (daysChanged) {
    body.tradingDays = draft.tradingDays;
    windowChanged = true;
  }
  return { body, windowChanged, changed: Object.keys(body).length > 0 };
}

function GroupFormDialog({
  target,
  globalForceCloseMinutes,
  globalForceCloseLabel,
  onClose,
  onDone,
}: {
  target: { mode: 'create' } | { mode: 'edit'; group: TradingGroup } | null;
  globalForceCloseMinutes: number | null;
  globalForceCloseLabel: string | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const editing = target?.mode === 'edit' ? target.group : null;
  const [draft, setDraft] = useState<GroupDraft>(emptyDraft());
  const [applyNow, setApplyNow] = useState(false);
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!target) return;
    setDraft(target.mode === 'edit' ? draftFromGroup(target.group) : emptyDraft());
    setApplyNow(false);
    setReason('');
  }, [target]);

  // ── 前端先行校验:不要只依赖后端 400,错了要当场看得见 ──
  const orderError = draft.alwaysOn ? null : checkGroupOrder(draft);
  const outOfTradeDay =
    !draft.alwaysOn && globalForceCloseMinutes != null && !windowWithinGlobalTradeDay(draft, globalForceCloseMinutes);
  const nameError = draft.name.trim().length === 0 ? '组名不能为空' : null;

  const diff = editing ? diffGroupDraft(draft, editing) : null;
  const windowChanged = diff ? diff.windowChanged : true;
  // 立即生效(绕过待生效)仍要求填写原因,写入审计。
  const needReason = !!editing && applyNow && windowChanged;
  const reasonOk = reason.trim().length >= 4;

  const submit = useMutation({
    mutationFn: () => {
      if (editing) {
        const body: UpdateTradingGroupRequest = { ...(diff?.body ?? {}) };
        if (applyNow && windowChanged) {
          body.applyNow = true;
          body.reason = reason.trim();
        }
        return adminTradingGroups.update(editing.id, body);
      }
      const body: CreateTradingGroupRequest = {
        name: draft.name.trim(),
        enabled: draft.enabled,
        isDefault: draft.isDefault,
        alwaysOn: draft.alwaysOn,
        openHour: draft.openHour,
        openMinute: draft.openMinute,
        windDownHour: draft.windDownHour,
        windDownMinute: draft.windDownMinute,
        liquidationHour: draft.liquidationHour,
        liquidationMinute: draft.liquidationMinute,
        forceCloseHour: draft.forceCloseHour,
        forceCloseMinute: draft.forceCloseMinute,
        tradingDays: draft.tradingDays,
      };
      return adminTradingGroups.create(body);
    },
    onSuccess: (res) => {
      const warns = (res?.validation?.issues ?? []).filter((i) => i.severity === 'warn');
      const desc = warns.length ? warns.map((i) => i.message).join('；') : undefined;
      if (!editing) {
        toast.success('时段组已创建', { description: desc ?? '新建即刻生效;把渠道或用户绑到该组后开始按新窗口交易' });
      } else if (applyNow && windowChanged) {
        toast.success('已立即生效', {
          description:
            desc ?? '窗口判定即时切换;该组的收盘 / 倒计时任务由 supervisor 每分钟复查,最长有一分钟对齐延迟',
        });
      } else if (windowChanged) {
        toast.success('已保存待生效', { description: desc ?? '窗口改动将于次日全局兜底后统一应用' });
      } else {
        toast.success('已保存', { description: desc });
      }
      onDone();
      onClose();
    },
    onError: (err) => toast.error(editing ? '保存失败' : '创建失败', { description: groupErrorMessage(err) }),
  });

  const blocked = !!nameError || !!orderError || outOfTradeDay;
  const canSubmit =
    !blocked &&
    !submit.isPending &&
    (!editing || diff!.changed) &&
    (!needReason || reasonOk);

  const toggleDay = (d: WeekDay) => {
    setDraft((x) => ({
      ...x,
      tradingDays: x.tradingDays.includes(d)
        ? x.tradingDays.filter((y) => y !== d)
        : [...x.tradingDays, d].sort(orderDays),
    }));
  };

  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && !submit.isPending && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-brand" />
            {editing ? `编辑时段组 · ${editing.name}` : '新建时段组'}
          </DialogTitle>
          <DialogDescription>
            {editing ? (
              <>
                窗口类改动(时刻 / 交易日 / 持续开放)<strong>默认次日全局兜底后生效</strong>;
                组名 / 启用 / 兜底组标记恒立即生效。要窗口也马上切换,请打开下方「立即生效」。
              </>
            ) : (
              <>新建组时组内尚无用户,<strong>创建即刻生效</strong>;之后在邀请码页或用户页把渠道 / 个人绑到该组。</>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="grp-name">组名</Label>
              <Input
                id="grp-name"
                placeholder="如:A 组 14-17"
                maxLength={64}
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                disabled={submit.isPending}
              />
              <p className="text-xs text-ink-3">仅管理端展示;App 端不出现组名</p>
            </div>
            <div className="space-y-2 pt-1">
              <label className="flex items-center gap-2 text-sm text-ink-1">
                <Switch
                  checked={draft.enabled}
                  onCheckedChange={(v) => setDraft({ ...draft, enabled: v })}
                  disabled={submit.isPending}
                />
                启用该组
                <span className="text-xs text-ink-3">(停用后组内用户回退到全局时段,绑定保留)</span>
              </label>
              <label className="flex items-center gap-2 text-sm text-ink-1">
                <Switch
                  checked={draft.isDefault}
                  onCheckedChange={(v) => setDraft({ ...draft, isDefault: v })}
                  disabled={submit.isPending}
                />
                设为兜底组
                <span className="text-xs text-ink-3">(全库至多一个;只作新建时的预选默认项)</span>
              </label>
            </div>
          </div>

          <div className="rounded-md border border-line bg-bg-2 px-3 py-2.5 space-y-2">
            <label className="flex items-center gap-2 text-sm text-ink-1">
              <Switch
                checked={draft.alwaysOn}
                onCheckedChange={(v) => setDraft({ ...draft, alwaysOn: v })}
                disabled={submit.isPending}
              />
              持续开放(24 小时组)
            </label>
            {draft.alwaysOn && (
              <div className="text-xs text-warn-dim leading-relaxed">
                持续开放,<strong>不参与日终结算</strong>:恒可交易、不调度收尾 / 结算 / 兜底,也不做倒计时提醒;
                额度重置 cron 整组排除(持仓不动、额度不重置,需管理员手动重置)。
                <br />
                ⚠ 能否成交仍受 cTrader 官方品种时段约束(黄金每日 05:00–06:00 前后休市、周末全休),
                休市下单会收到券商原始拒单;平台级急停触发时本组同样会被平仓。
              </div>
            )}
          </div>

          {!draft.alwaysOn && (
            <>
              {/* 四边界与全局时段配置同形。顺序按「环序」约束:开仓 → 收尾 → 结算 → 兜底(可跨零点)。 */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <TimeField
                  label="开仓时刻"
                  hour={draft.openHour}
                  minute={draft.openMinute}
                  onChange={(h, m) => setDraft({ ...draft, openHour: h, openMinute: m })}
                />
                <TimeField
                  label="收尾开始"
                  hour={draft.windDownHour}
                  minute={draft.windDownMinute}
                  onChange={(h, m) => setDraft({ ...draft, windDownHour: h, windDownMinute: m })}
                  hint="起只许平仓,不许开新仓"
                />
                <TimeField
                  label="结算时刻"
                  hour={draft.liquidationHour}
                  minute={draft.liquidationMinute}
                  onChange={(h, m) => setDraft({ ...draft, liquidationHour: h, liquidationMinute: m })}
                  hint="本组日终结算起点(只动本组腿)"
                />
                <TimeField
                  label="兜底时刻"
                  hour={draft.forceCloseHour}
                  minute={draft.forceCloseMinute}
                  onChange={(h, m) => setDraft({ ...draft, forceCloseHour: h, forceCloseMinute: m })}
                  hint="本组静默起点(不是交易日界)"
                />
              </div>

              <div className="space-y-2">
                <Label>交易日</Label>
                <div className="flex flex-wrap gap-2">
                  {ALL_DAYS.map((d) => {
                    const active = draft.tradingDays.includes(d);
                    return (
                      <button
                        key={d}
                        type="button"
                        disabled={submit.isPending}
                        onClick={() => toggleDay(d)}
                        className={cn(
                          'rounded-md border px-3 py-1.5 text-sm transition-colors btn-press',
                          active
                            ? 'border-brand bg-brand/10 text-brand-dim font-medium'
                            : 'border-line bg-bg-2 text-ink-2 hover:bg-bg-3',
                        )}
                      >
                        {d}
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {/* 前端校验提示:不要只依赖后端 400 */}
          {nameError && <FormError>{nameError}</FormError>}
          {orderError && <FormError>{orderError}(须按 开仓 → 收尾 → 结算 → 兜底 依次推进,可跨零点)</FormError>}
          {outOfTradeDay && (
            <FormError>
              窗口越出全局交易日:开仓 {fmtHM(draft.openHour, draft.openMinute)} 到兜底{' '}
              {fmtHM(draft.forceCloseHour, draft.forceCloseMinute)} 跨过了全局兜底
              {globalForceCloseLabel ? ` ${globalForceCloseLabel}` : ''}(交易日翻篇点)。
              日界不分组,组窗口必须完整落在同一个交易日内。
            </FormError>
          )}

          {editing && (
            <div className="rounded-md border border-line bg-bg-2 px-3 py-2.5 space-y-2">
              <label className="flex items-center gap-2 text-sm text-ink-1">
                <Switch
                  checked={applyNow}
                  onCheckedChange={setApplyNow}
                  disabled={submit.isPending || !windowChanged}
                />
                立即生效(绕过待生效)
                {!windowChanged && <span className="text-xs text-ink-3">(本次没有窗口类改动,无需)</span>}
              </label>
              {needReason && (
                <div className="space-y-1.5">
                  <Label htmlFor="grp-reason">操作原因(至少 4 字)</Label>
                  <Input
                    id="grp-reason"
                    placeholder="例如:该渠道今晚提前收盘"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    disabled={submit.isPending}
                  />
                </div>
              )}
            </div>
          )}

          {editing?.validation && !editing.alwaysOn && (
            <div className="space-y-1">
              <div className="text-xs text-ink-3">按「已保存」配置对照 cTrader 官方窗口的校验结果:</div>
              <ValidationBanner v={editing.validation} />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submit.isPending}>
            取消
          </Button>
          <Button onClick={() => submit.mutate()} disabled={!canSubmit}>
            {submit.isPending ? '保存中...' : editing ? (applyNow && windowChanged ? '立即生效' : '保存') : '创建'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FormError({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-down/40 bg-down/10 px-3 py-2 text-xs text-down leading-relaxed">
      {children}
    </div>
  );
}

// ─── 删除 ─────────────────────────────────────────────────────────

function DeleteGroupDialog({
  target,
  onClose,
  onDone,
}: {
  target: TradingGroup | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const remove = useMutation({
    mutationFn: () => adminTradingGroups.remove(target!.id),
    onSuccess: () => {
      toast.success('时段组已删除', {
        description: '历史分组日报不受影响(虚拟腿的组归属是开仓时快照,日报另存组名快照)',
      });
      onDone();
      onClose();
    },
    onError: (err) => {
      // 409 GROUP_IN_USE:后端拒绝静默解绑(那会让一批用户在无人察觉的情况下改回全局窗口)。
      if (err instanceof ApiError && err.code === 'GROUP_IN_USE') {
        const codes = Number(err.details?.['boundInviteCodes'] ?? 0);
        const users = Number(err.details?.['boundUsers'] ?? 0);
        const parts: string[] = [];
        if (codes > 0) parts.push(`${codes} 个渠道(邀请码)`);
        if (users > 0) parts.push(`${users} 个用户`);
        toast.error('该组仍有绑定,不能删除', {
          description: parts.length
            ? `请先解绑 ${parts.join(' 和 ')}:渠道到「邀请码管理」改时段组,用户到「注册用户管理」清除个人覆盖`
            : '请先解绑该组下的渠道与用户',
        });
        return;
      }
      toast.error('删除失败', { description: apiMessage(err) });
    },
  });

  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && !remove.isPending && onClose()}>
      <DialogContent className="max-w-md">
        {target && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-down">
                <Trash2 className="h-5 w-5" />
                删除时段组 · {target.name}
              </DialogTitle>
              <DialogDescription>
                删除后该组不再调度收盘 / 倒计时。<strong>仍有渠道或用户绑定时会被拒绝</strong>
                (需先手动解绑,系统不做静默解绑 —— 那会让这批人在无人察觉的情况下改回全局窗口)。
              </DialogDescription>
            </DialogHeader>
            <div className="text-sm text-ink-2 space-y-1">
              <div>
                当前在组人数:<strong className="num text-ink-1">{target.userCount}</strong>;
                绑定渠道:<strong className="num text-ink-1">{target.boundInviteCodes.length}</strong> 个
              </div>
              <div className="text-xs text-ink-3">
                历史数据不受影响:虚拟腿的组归属是开仓时的快照,分组日报另存组名快照,删组后历史仍可读。
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose} disabled={remove.isPending}>
                取消
              </Button>
              <Button variant="destructive" onClick={() => remove.mutate()} disabled={remove.isPending}>
                {remove.isPending ? '删除中…' : '确认删除'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── 立即应用待生效 ───────────────────────────────────────────────

function ApplyGroupPendingDialog({
  target,
  onClose,
  onDone,
}: {
  target: TradingGroup | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (target) {
      setReason('');
    }
  }, [target]);

  const run = useMutation({
    mutationFn: () => adminTradingGroups.applyPendingNow(target!.id, { reason: reason.trim() }),
    onSuccess: () => {
      toast.success('该组待生效配置已立即生效', {
        description: '只作用于本组;收盘 / 倒计时任务由 supervisor 每分钟复查配置,最长有一分钟对齐延迟',
      });
      onDone();
      onClose();
    },
    onError: (err) => toast.error('立即生效失败', { description: apiMessage(err) }),
  });

  const canSubmit = reason.trim().length >= 4 && !run.isPending;

  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && !run.isPending && onClose()}>
      <DialogContent className="max-w-md">
        {target && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-warn">
                <Zap className="h-5 w-5" />
                立即生效 · {target.name}
              </DialogTitle>
              <DialogDescription>
                无视待生效时刻,立即把该组的待生效配置写入生效值。<strong>仅作用于本组</strong>
                (不波及全局时段、其他组、品种 / 风控的待生效)。
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <PendingGroupDiff group={target} />
              <div className="space-y-1.5">
                <Label htmlFor="grp-apn-reason">操作原因(至少 4 字)</Label>
                <Input
                  id="grp-apn-reason"
                  placeholder="例如:该渠道今晚提前切换新窗口"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  disabled={run.isPending}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose} disabled={run.isPending}>
                取消
              </Button>
              <Button variant="warn" onClick={() => run.mutate()} disabled={!canSubmit}>
                {run.isPending ? '生效中...' : '立即生效'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── 错误文案 ─────────────────────────────────────────────────────

function apiMessage(err: unknown): string {
  return err instanceof ApiError ? err.message : '未知错误';
}

/**
 * 把时段组特有的三类 400 翻成人话(后端 message 之外补上「该怎么改」)。
 *
 * ⚠ 后端这三类都走 `ApiError::ValidationWithDetails`,**顶级 `code` 恒为 `VALIDATION`**,
 * 具体码只出现在 `details.issues[].code` 里(errors.md 已按此口径更正)。所以必须先扫 issues,
 * 不能只看 `err.code` —— 否则顺序/越界错误会掉进最后那句「配置落在 cTrader 休市窗口」,
 * 把「时刻顺序填错」误导成「黄金休市」,管理员会去改品种时段而不是改顺序。
 */
function groupErrorMessage(err: unknown): string {
  if (!(err instanceof ApiError)) return '未知错误';
  const issues = (err.details?.['issues'] as SessionWindowIssue[] | undefined) ?? [];
  const errors = Array.isArray(issues) ? issues.filter((i) => i.severity === 'error') : [];
  const hasCode = (code: string) =>
    err.code === code || errors.some((i) => i.code === code);

  if (hasCode('INVALID_HOUR_ORDER')) {
    const msg = errors.find((i) => i.code === 'INVALID_HOUR_ORDER')?.message ?? err.message;
    return `${msg}(须按 开仓 → 收尾 → 结算 → 兜底 依次推进)`;
  }
  if (hasCode('INVALID_GROUP_WINDOW')) {
    const gfc = err.details?.['globalForceClose'];
    const msg = errors.find((i) => i.code === 'INVALID_GROUP_WINDOW')?.message ?? err.message;
    return `${msg}${gfc ? ` · 全局兜底 ${String(gfc)}` : ''}(组窗口必须完整落在同一个全局交易日内)`;
  }
  if (errors.length) return `配置落在 cTrader 休市窗口:${errors.map((i) => i.message).join('；')}`;
  return err.message;
}
